/* ──────────────────────────────────────────────────────────────────────────
 *  Ecommerce analytics helpers — convert app-level Cart / Product objects
 *  into GA4 Enhanced Ecommerce shapes and fire through the analytics core.
 * ────────────────────────────────────────────────────────────────────────── */

import { analytics } from "./core";
import type { EcommerceItem } from "./types";
import type { CartItem } from "@/context/CartContext";

const CURRENCY = "CAD";

/* ── Converters ────────────────────────────────────────────────────────── */

export function cartItemToEcomItem(
  item: CartItem,
  index?: number,
): EcommerceItem {
  return {
    item_id: item.sku || item.id,
    item_name: item.name,
    item_variant: item.variant,
    item_category: item.dosageForm,
    price: item.price,
    quantity: item.quantity,
    currency: CURRENCY,
    ...(index !== undefined ? { index } : {}),
  };
}

export function productToEcomItem(product: {
  databaseId?: number;
  slug: string;
  name: string;
  sku?: string | null;
  price?: number;
  category?: string;
}): EcommerceItem {
  return {
    item_id: product.sku || String(product.databaseId ?? product.slug),
    item_name: product.name,
    item_category: product.category,
    price: product.price,
    quantity: 1,
    currency: CURRENCY,
  };
}

/* ── Event helpers ─────────────────────────────────────────────────────── */

export function trackViewItem(item: EcommerceItem) {
  analytics.track("view_item", {
    currency: CURRENCY,
    value: (item.price ?? 0) * (item.quantity ?? 1),
    items: [item],
  });
}

export function trackViewItemList(
  items: EcommerceItem[],
  listId?: string,
  listName?: string,
) {
  analytics.track("view_item_list", {
    item_list_id: listId,
    item_list_name: listName,
    items,
  });
}

export function trackSelectItem(
  item: EcommerceItem,
  listId?: string,
  listName?: string,
) {
  analytics.track("select_item", {
    item_list_id: listId,
    item_list_name: listName,
    items: [item],
  });
}

/** Source of an ecommerce action.  Defaults to "self_serve" when the
 *  user clicks add-to-cart themselves; pass "agent" when the voice
 *  agent initiated the action so GA4 funnels split agent-attributed
 *  revenue without parallel-emit (Phase 1 #7). */
export type EcommerceSource = "self_serve" | "agent";

export function trackAddToCart(
  item: CartItem,
  source: EcommerceSource = "self_serve",
) {
  const ecom = cartItemToEcomItem(item);
  analytics.track("add_to_cart", {
    currency: CURRENCY,
    value: item.price * item.quantity,
    items: [ecom],
    source,
  });
}

export function trackRemoveFromCart(
  item: CartItem,
  source: EcommerceSource = "self_serve",
) {
  const ecom = cartItemToEcomItem(item);
  analytics.track("remove_from_cart", {
    currency: CURRENCY,
    value: item.price * item.quantity,
    items: [ecom],
    source,
  });
}

export function trackViewCart(cartItems: CartItem[]) {
  const items = cartItems.map((ci, i) => cartItemToEcomItem(ci, i));
  const value = cartItems.reduce(
    (sum, ci) => sum + ci.price * ci.quantity,
    0,
  );
  analytics.track("view_cart", { currency: CURRENCY, value, items });
}

export function trackBeginCheckout(
  cartItems: CartItem[],
  coupon?: string,
  source: EcommerceSource = "self_serve",
) {
  const items = cartItems.map((ci, i) => cartItemToEcomItem(ci, i));
  const value = cartItems.reduce(
    (sum, ci) => sum + ci.price * ci.quantity,
    0,
  );
  analytics.track("begin_checkout", {
    currency: CURRENCY,
    value,
    items,
    coupon,
    source,
  });
}

export function trackAddShippingInfo(
  cartItems: CartItem[],
  shippingTier?: string,
) {
  const items = cartItems.map((ci, i) => cartItemToEcomItem(ci, i));
  const value = cartItems.reduce(
    (sum, ci) => sum + ci.price * ci.quantity,
    0,
  );
  analytics.track("add_shipping_info", {
    currency: CURRENCY,
    value,
    shipping_tier: shippingTier,
    items,
  });
}

export function trackPurchase(
  order: {
    transactionId: string;
    value: number;
    tax?: number;
    shipping?: number;
    items: EcommerceItem[];
  },
  source: EcommerceSource = "self_serve",
) {
  analytics.track("purchase", {
    transaction_id: order.transactionId,
    currency: CURRENCY,
    value: order.value,
    tax: order.tax,
    shipping: order.shipping,
    items: order.items,
    source,
  });
}
